/**
 * Onboarding wizard — first-run modal that captures organisation industry,
 * functional domain, and role so roleserver can generate a personalized
 * starter-card stack (brainstorm-z8t2.7).
 *
 * Steps:
 *   1. Organisation industry (static select, org-level vertical)
 *   2. Functional domain / group_function (static list, mirrors the 9
 *      roleserver FSM domain YAMLs)
 *   3. Role (reuses the useRoles + custom-role pattern from RoleModal.jsx)
 *
 * Submit order matters: PATCH /user/organisation (industry) fires BEFORE
 * PATCH /user/profile (role, group_function) — the profile PATCH is what
 * triggers backend card generation, and it reads industry off the org row
 * at that point. A non-admin member gets a 403 on the org PATCH; we swallow
 * that and still submit the profile PATCH so onboarding always completes.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useRoles } from '../hooks/useRoles';
import Card from './ui/Card';
import Button from './ui/Button';

const INDUSTRIES = [
  'Consulting',
  'Finance',
  'Healthcare',
  'Legal',
  'Technology',
  'Retail',
  'Manufacturing',
  'Other',
];

// Mirrors roleserver's 9 domain FSM YAMLs (app/data/domain_fsms/*.yaml) exactly.
const GROUP_FUNCTIONS = [
  'Sales',
  'Marketing',
  'Finance & Accounting',
  'Customer Success',
  'People & HR',
  'Operations',
  'Legal & Compliance',
  'Product & Engineering',
  'Support',
];

const TOTAL_STEPS = 3;

export default function OnboardingWizard({ onComplete }) {
  const { roles: availableRoles } = useRoles();

  const [step, setStep] = useState(1);
  const [industry, setIndustry] = useState('');
  const [groupFunction, setGroupFunction] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [useCustomRole, setUseCustomRole] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSkip = () => {
    localStorage.setItem('onboarding_skipped', '1');
    onComplete();
  };

  const goNext = () => {
    setError('');
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const goBack = () => {
    setError('');
    setStep((s) => Math.max(s - 1, 1));
  };

  const handleSubmit = async () => {
    const roleToSave = useCustomRole ? customRole.trim() : selectedRole;

    if (!roleToSave) {
      setError('Please select or enter a role');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // 1. Organisation industry — order matters, must land before the
      // profile PATCH so backend card generation sees it. Non-admin org
      // members get a 403 here; that's expected and non-fatal.
      if (industry) {
        try {
          await axios.patch('/user/organisation', { industry });
        } catch (err) {
          if (err?.response?.status === 403) {
            console.log('Skipping industry — not org admin');
          } else {
            console.error('Failed to update organisation industry:', err);
          }
        }
      }

      // If using a custom role, register it first (best-effort, same as RoleModal).
      if (useCustomRole && customRole.trim()) {
        try {
          await axios.post(`/api/roles/${encodeURIComponent(customRole.trim())}`, {
            label: customRole.trim(),
          });
        } catch (err) {
          console.error('Failed to save custom role:', err);
        }
      }

      // 2. Profile — role + group_function. Triggers card generation.
      await axios.patch('/user/profile', {
        role: roleToSave,
        group_function: groupFunction,
      });

      // Hand the saved values back so the caller can poll for the NEW
      // profile's cards rather than latching the previous set (z8t2.19).
      onComplete({ role: roleToSave, groupFunction });
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
      <Card
        variant="static"
        className="w-full max-w-md p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-wizard-title"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 id="onboarding-wizard-title" className="text-lg font-semibold text-gray-900">
            Let's set up your workspace
          </h2>
          <span className="text-xs text-gray-500">
            Step {step} of {TOTAL_STEPS}
          </span>
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <label htmlFor="onboarding-industry" className="block text-sm text-gray-700">
              What industry is your organisation in?
            </label>
            <select
              id="onboarding-industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              disabled={loading}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus-ring"
            >
              <option value="">Select an industry</option>
              {INDUSTRIES.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <label htmlFor="onboarding-group-function" className="block text-sm text-gray-700">
              Which functional domain do you work in?
            </label>
            <select
              id="onboarding-group-function"
              value={groupFunction}
              onChange={(e) => setGroupFunction(e.target.value)}
              disabled={loading}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus-ring"
            >
              <option value="">Select a functional domain</option>
              {GROUP_FUNCTIONS.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="onboarding-role" className="block text-sm text-gray-700">
                Select your role
              </label>
              <select
                id="onboarding-role"
                value={selectedRole}
                onChange={(e) => {
                  setSelectedRole(e.target.value);
                  setUseCustomRole(false);
                }}
                disabled={loading || useCustomRole}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus-ring disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">Select a role</option>
                {/* Save the human-readable label, not the underscored key —
                    the role feeds card-generation prompts and profile display. */}
                {availableRoles.map((roleObj) => (
                  <option key={roleObj.role} value={roleObj.label || roleObj.role}>
                    {roleObj.label || roleObj.role}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-400">
              <div className="h-px flex-1 bg-gray-200" />
              or
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            <div className="space-y-2">
              <label htmlFor="onboarding-custom-role" className="block text-sm text-gray-700">
                Enter a custom role
              </label>
              <input
                id="onboarding-custom-role"
                type="text"
                maxLength={100}
                value={customRole}
                onChange={(e) => {
                  setCustomRole(e.target.value);
                  if (e.target.value.trim()) {
                    setUseCustomRole(true);
                    setSelectedRole('');
                  } else {
                    setUseCustomRole(false);
                  }
                }}
                disabled={loading}
                placeholder="e.g. product manager, team lead"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus-ring disabled:bg-gray-50"
              />
            </div>
          </div>
        )}

        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

        <div className="mt-6 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={handleSkip} disabled={loading}>
            Skip for now
          </Button>

          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button variant="secondary" size="sm" onClick={goBack} disabled={loading}>
                Back
              </Button>
            )}
            {step < TOTAL_STEPS && (
              <Button
                variant="primary"
                size="sm"
                onClick={goNext}
                disabled={loading || (step === 1 && !industry) || (step === 2 && !groupFunction)}
              >
                Next
              </Button>
            )}
            {step === TOTAL_STEPS && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                disabled={loading || (!selectedRole && !customRole.trim())}
              >
                {loading ? 'Saving...' : 'Finish'}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
